import React from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import type { ActionButtonType } from '@klp/design';
import { ActionButton, ActionLink, Block, Heading } from '@klp/design';
import { Paragraph } from '@klp/design/Typography/Typography/Paragraph';

import ErrorImage from './assets/error-image.png';

type ErrorNotificationType = 'Default' | 'Alert' | 'Extended';

export interface IErrorNotificationProps {
    title?: string;
    subtitle?: string;
    description?: string;
    backTarget?: string;
    backTitle?: string;
    backClickHandler?: () => void;
    mainTarget?: string;
    mainTitle?: string;
    mainClickHandler?: () => void;
    returnUrl?: boolean;
    type?: ErrorNotificationType;
    mainTargetButtonType?: keyof typeof ActionButtonType;
    secondTitle?: string;
    secondDescription?: string;
    thirdTitle?: string;
    thirdDescription?: string;
    children?: React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) => ({
    title: {
        fontFamily: theme.typography.fontFamily,
    },
    buttonsContainer: {
        display: 'flex',
    },
    errorImage: {
        '& > img': {
            width: '100%',
            minHeight: '1px', // IE11 hack
            maxWidth: '100%',
        },
    },
    alertContainer: {
        backgroundColor: theme.palette.secondary.veryLight,
        border: '1px solid transparent',
        borderColor: theme.palette.secondary.main,
    },
    alertIcon: {
        width: '1em',
        height: '1em',
        fontSize: '2em',
    },
}));

export const ErrorNotification: React.FC<IErrorNotificationProps> = ({
    title = '',
    subtitle,
    description,
    backTarget,
    backTitle,
    backClickHandler,
    mainTarget,
    mainTitle,
    mainClickHandler,
    returnUrl = false,
    type = 'Default',
    mainTargetButtonType,
    secondTitle,
    secondDescription,
    thirdTitle,
    thirdDescription,
    children,
}) => {
    const classes = useStyles();
    const location = useLocation();

    const targetParams = returnUrl
        ? { returnUrl: location.pathname }
        : undefined;

    const renderAlertNotification = () => {
        return (
            <Block marginVertical="bulky">
                <Grid
                    container={true}
                    alignItems="center"
                    className={classes.alertContainer}
                >
                    <Grid item={true} xs={12}>
                        <Block padding="compact">
                            {title && (
                                <Heading
                                    klpHeadingLevel="TWO"
                                    klpColor="dark"
                                    translation={title}
                                />
                            )}
                            {description && (
                                <Paragraph translation={description} />
                            )}
                        </Block>
                    </Grid>
                </Grid>
            </Block>
        );
    };

    const renderExtendedNotification = () => {
        return (
            <Grid container={true}>
                <Grid item={true} xs={12} sm={6}>
                    <Block>
                        <Grid container={true} spacing={2}>
                            {title && (
                                <Grid item={true} xs={12}>
                                    <Heading
                                        klpHeadingLevel="TWO"
                                        translation={title}
                                        data-testid="ErrorNotification__Title"
                                    />
                                </Grid>
                            )}
                            {subtitle && (
                                <Grid item={true} xs={12}>
                                    <Heading
                                        klpHeadingLevel="THREE"
                                        translation={subtitle}
                                    />
                                </Grid>
                            )}
                            {description && (
                                <Grid item={true} xs={12}>
                                    <Paragraph
                                        translation={description}
                                        data-testid="ErrorNotification__Description"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Block>
                    <Block marginTop="tiny">
                        {(mainTarget || mainClickHandler) && mainTitle && (
                            <Block>
                                <ActionButton
                                    actionType={
                                        mainTargetButtonType || 'forward'
                                    }
                                    translation={mainTitle}
                                    target={mainTarget}
                                    targetParams={targetParams}
                                    onClick={mainClickHandler}
                                />
                            </Block>
                        )}
                        {secondTitle && secondDescription && (
                            <Block paddingTop="fat">
                                <Heading
                                    klpHeadingLevel="FIVE"
                                    translation={secondTitle}
                                />
                                <Block
                                    paddingTop="tiny"
                                    translation={secondDescription}
                                />
                            </Block>
                        )}

                        {(backTarget || backClickHandler) && backTitle && (
                            <Block marginTop="tiny">
                                <ActionLink
                                    actionType="link"
                                    href={backTarget}
                                    translation={backTitle}
                                    target={backTarget}
                                    onClick={backClickHandler}
                                />
                            </Block>
                        )}
                        {thirdTitle && thirdDescription && (
                            <Block paddingTop="fat">
                                <Heading
                                    klpHeadingLevel="FIVE"
                                    translation={thirdTitle}
                                />
                                <Block
                                    paddingTop="tiny"
                                    translation={thirdDescription}
                                />
                            </Block>
                        )}
                    </Block>
                    {children}
                </Grid>
                <Grid item={true} xs={12} sm={6}>
                    <Block className={classes.errorImage} paddingTop="jumbo">
                        <img src={ErrorImage} alt="Error image" />
                    </Block>
                </Grid>
            </Grid>
        );
    };

    const renderDefaultNotification = () => {
        return (
            <Grid container={true}>
                <Grid item={true} xs={12} sm={6}>
                    <Block>
                        <Grid container={true} spacing={2}>
                            {title && (
                                <Grid item={true} xs={12}>
                                    <Heading
                                        klpHeadingLevel="TWO"
                                        translation={title}
                                        data-testid="ErrorNotification__Title"
                                    />
                                </Grid>
                            )}
                            {subtitle && (
                                <Grid item={true} xs={12}>
                                    <Heading
                                        klpHeadingLevel="THREE"
                                        translation={subtitle}
                                    />
                                </Grid>
                            )}
                            {description && (
                                <Grid item={true} xs={12}>
                                    <Paragraph
                                        translation={description}
                                        data-testid="ErrorNotification__Description"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Block>
                    <Block marginTop="compact">
                        {(mainTarget || mainClickHandler) && mainTitle && (
                            <Block>
                                <ActionButton
                                    actionType={
                                        mainTargetButtonType || 'forward'
                                    }
                                    translation={mainTitle}
                                    target={mainTarget}
                                    targetParams={targetParams}
                                    onClick={mainClickHandler}
                                />
                            </Block>
                        )}
                        {(backTarget || backClickHandler) && backTitle && (
                            <Block marginTop="bulky">
                                <ActionLink
                                    actionType="link"
                                    href={backTarget}
                                    translation={backTitle}
                                    target={backTarget}
                                    onClick={backClickHandler}
                                />
                            </Block>
                        )}
                    </Block>
                    {children}
                </Grid>
                <Grid item={true} xs={12} sm={6}>
                    <div className={classes.errorImage}>
                        <img src={ErrorImage} alt="Error image" />
                    </div>
                </Grid>
            </Grid>
        );
    };

    if (type === 'Alert') {
        return renderAlertNotification();
    }
    if (type === 'Extended') {
        return renderExtendedNotification();
    }

    return renderDefaultNotification();
};
